import React, { useEffect, useState } from 'react';
import { Box, Button, Heading, Layer, Spinner, Text } from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import QueryString from 'qs';
import { API } from '../../api';
import { NoteCard } from './NoteCard';

// Strapi's REST maxLimit; large enough to show every note for one contact.
const NOTES_PAGE_SIZE = 1000;

// Read-only modal listing a contact's notes, newest first. Notes load only
// when the modal mounts, so list pages never pay for note bodies up front.
export const ContactNotesModal = ({ contactId, contactName, onClose }) => {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let isActive = true;
        setIsLoading(true);
        setHasError(false);

        (async () => {
            const query = QueryString.stringify(
                {
                    filters: { contact: { id: { $eq: contactId } } },
                    sort: 'createdAt:desc',
                    pagination: { pageSize: NOTES_PAGE_SIZE },
                },
                { encodeValuesOnly: true },
            );

            try {
                const response = await API.get(`/api/contact-notes?${query}`);
                if (isActive) setNotes(response.data.data ?? []);
            } catch (error) {
                console.log(error);
                if (isActive) setHasError(true);
            }
            if (isActive) setIsLoading(false);
        })();

        return () => {
            isActive = false;
        };
    }, [contactId]);

    const renderBody = () => {
        if (isLoading) return <Spinner alignSelf="center" />;
        if (hasError) return <Text>Could not load notes.</Text>;
        if (notes.length === 0) return <Text>No notes.</Text>;
        return notes.map((note) => <NoteCard key={note.id} note={note} />);
    };

    return (
        <Layer onClickOutside={onClose} onEsc={onClose} position="center">
            <Box
                background="white"
                pad="medium"
                width="large"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                gap="medium">
                <Box
                    direction="row"
                    justify="between"
                    align="center"
                    flex={false}>
                    <Heading level="3" margin="none">
                        {`Notes — ${contactName}`}
                    </Heading>
                    <Button
                        a11yTitle="Close notes"
                        icon={<Close />}
                        onClick={onClose}
                    />
                </Box>
                <Box
                    overflow="auto"
                    height={{ max: 'large' }}
                    gap="small"
                    pad="xsmall">
                    {renderBody()}
                </Box>
            </Box>
        </Layer>
    );
};

ContactNotesModal.propTypes = {
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    contactName: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};
